import { appConfig } from "@/config";

const TOKEN_KEY = "cp_admin_token";

/** Allowed Control Plane API prefixes for the Operations Dashboard. */
const ALLOWED_API_PREFIXES = [
  "/api/auth/",
  "/api/platform/",
  "/api/customers/",
  "/api/verification/",
] as const;

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

function apiBase(): string {
  return appConfig.apiBase;
}

function resolveApiPath(path: string): string {
  if (!path.startsWith("/api/")) {
    throw new Error(`Invalid API path (must start with /api/): ${path}`);
  }
  const allowed = ALLOWED_API_PREFIXES.some((prefix) => path.startsWith(prefix));
  if (!allowed) {
    throw new Error(`Blocked API path outside Operations scope: ${path}`);
  }
  return `${apiBase()}${path}`;
}

export type DoctorSummaryApi = {
  pass_count: number;
  warn_count: number;
  fail_count: number;
  skip_count: number;
};

export type StorageHealthApi = {
  band: "green" | "yellow" | "orange" | "red" | "critical" | "unknown";
  filesystem_path: string;
  filesystem_total_bytes?: number | null;
  filesystem_used_bytes?: number | null;
  filesystem_free_bytes?: number | null;
  filesystem_used_percent?: number | null;
  docker_images_bytes?: number | null;
  docker_build_cache_bytes?: number | null;
  docker_volumes_bytes?: number | null;
  docker_containers_bytes?: number | null;
  docker_logs_bytes?: number | null;
  journal_bytes?: number | null;
  largest_consumers?: Array<{ name: string; bytes: number; detail?: string | null }>;
  recommended_cleanup?: string[];
  last_cleanup?: {
    started_at?: string | null;
    duration_seconds?: number | null;
    images_removed_count?: number;
    space_reclaimed_bytes?: number | null;
    cache_reclaimed_bytes?: number | null;
    free_bytes_after?: number | null;
    ok?: boolean;
  } | null;
  messages?: string[];
  collected_at: string;
  log_rotation_ok?: boolean | null;
  log_rotation_detail?: string | null;
  deploy_blocked?: boolean;
  findings?: Array<{
    id: string;
    severity: string;
    problem: string;
    recommendation?: string | null;
  }>;
  history?: Array<{
    collected_at?: string | null;
    band?: string | null;
    filesystem_used_percent?: number | null;
    filesystem_free_bytes?: number | null;
  }>;
};

export type PlatformPulseApi = {
  status: "healthy" | "degraded" | "critical";
  active_tenants: number;
  total_tenants: number;
  runtime_doctor_status: "healthy" | "degraded" | "critical";
  runtime_doctor_verdict: "healthy" | "degraded" | "unhealthy" | "unknown";
  doctor_summary: DoctorSummaryApi;
  deployments_running: number;
  last_runtime_doctor: string | null;
  last_backup: string | null;
  last_checked: string;
  control_plane_status: "ok" | "unhealthy";
  tenants_doctor_scanned: number;
  storage_health?: StorageHealthApi | null;
};

export type AttentionIncidentApi = {
  id: string;
  tenant_slug: string;
  tenant_name: string;
  severity: "critical" | "high" | "medium" | "low";
  check_id: string;
  problem: string;
  recommended_action: string;
  occurred_at: string;
};

export type RuntimeProbeApi = {
  status: string;
  message: string | null;
  check_id: string | null;
  latency_ms?: number | null;
  recommendation?: string | null;
  verified_at?: string | null;
} | null;

export type RuntimeProbesApi = {
  control_plane_backend?: RuntimeProbeApi;
  operations_api?: RuntimeProbeApi;
  authentication?: RuntimeProbeApi;
  internal_platform_services?: RuntimeProbeApi;
  email_processing?: RuntimeProbeApi;
  worker?: RuntimeProbeApi;
  database?: RuntimeProbeApi;
  disk?: RuntimeProbeApi;
  memory?: RuntimeProbeApi;
  docker?: RuntimeProbeApi;
  cpu?: RuntimeProbeApi;
};

export type TenantHealthApi = {
  slug: string;
  display_name: string;
  health: "healthy" | "degraded" | "critical";
  doctor_status: "pass" | "warn" | "fail" | "unknown" | "not_scanned";
  platform_version: string | null;
  ai_version: string | null;
  last_backup: string | null;
  /** Size of postgres.sql in the newest verified archive (bytes). */
  last_backup_bytes?: number | null;
  /** Absolute path to postgres.sql in the newest verified archive. */
  last_backup_path?: string | null;
  /** Modification time of postgres.sql in the newest verified archive. */
  last_backup_mtime?: string | null;
  /** SHA-256 of postgres.sql when available (sidecar). */
  last_backup_checksum?: string | null;
  open_alerts: number;
  stack_port: number | null;
  has_stack: boolean;
  /** Runtime Doctor subsystem probes — absent/null on legacy tenants. */
  runtime_probes?: RuntimeProbesApi | null;
  /** VerificationResult records from Runtime Doctor for this tenant. */
  verifications?: Array<Record<string, unknown>> | null;
};

export type OperatorTaskApi = {
  id: string;
  severity: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  explanation: string;
  action_label: string;
  action: "run-doctor" | null;
  incident_id: string;
};

export type IncidentEvidenceApi = {
  kind: string;
  category: string;
  label: string;
  value: string;
  observed_at: string;
};

export type IncidentPolicyApi = {
  policy: string;
  status: string;
  severity: "critical" | "high" | "medium" | "low" | null;
  confidence: "high" | "medium" | "low" | null;
  reason: string;
  recommendation: string | null;
  evaluated_at: string;
};

export type TimelineEventApi = {
  timestamp: string;
  component: string;
  event_type:
    | "deployment"
    | "verification"
    | "policy_evaluation"
    | "recovery"
    | "incident_opened"
    | "incident_resolved"
    | "other";
  status: string;
  label: string;
  detail: string;
  verification_ref: string | null;
  policy_ref: string | null;
  incident_id: string | null;
  deployment_id: string | null;
};

export type ExcludedCauseApi = { cause: string; reason: string };

export type RootCauseAnalysisApi = {
  summary: string;
  method: "deployment_correlation" | "policy_evaluation" | "single_component_failure" | "insufficient_evidence";
  confidence: number;
  confidence_label: "high" | "medium" | "low";
  supporting_evidence: string[];
  excluded_causes: ExcludedCauseApi[];
  inputs: Record<string, unknown>;
};

export type OperationalImpactApi = {
  affected_capabilities: string[];
  affected_tenants: string[];
  estimated_severity: "critical" | "high" | "medium" | "low";
  business_impact: string;
};

export type RecommendationApi = {
  action: string;
  reason: string;
  confidence: number;
  confidence_label: "high" | "medium" | "low";
  expected_outcome: string;
  supporting_evidence: string[];
  action_id: string | null;
  automation_level?: "safe" | "confirm" | "manual" | null;
  risk?: "safe" | "low" | "medium" | "high" | null;
  estimated_duration_seconds?: number | null;
  requires_confirmation?: boolean;
};

export type IncidentNarrativeApi = {
  summary: string;
  root_cause: string;
  impact: string;
  evidence: string[];
  recommendations: string[];
  confidence: string;
  full_text: string;
};

export type IncidentRelationshipsApi = {
  parent_incident_id: string | null;
  child_incident_ids: string[];
  duplicate_incident_ids: string[];
  recurring: boolean;
  recurrence_count: number;
  related_deployment_ids: string[];
  related_maintenance_windows: string[];
};

export type IncidentDetailApi = {
  id: string;
  title: string;
  severity: "critical" | "high" | "medium" | "low";
  status: "open" | "investigating" | "monitoring" | "resolved";
  lifecycle_phase: IncidentLifecyclePhaseApi;
  lifecycle_label: string;
  tenant_slug: string;
  tenant_name: string;
  category: string;
  detected_at: string;
  last_verified_at: string;
  explanation: string;
  impact: {
    customer_impact: string;
    business_impact: string;
    platform_impact: string;
    estimated_urgency: "high" | "medium" | "low";
  };
  evidence_summary: string;
  evidence: IncidentEvidenceApi[];
  recommended_action: string;
  assistant_briefing: string;
  ai_explanation: string;
  ai_reasoning: string;
  ai_confidence: number | null;
  confidence_reasons: string[];
  mitigation: IncidentMitigationApi | null;
  actions: { id: string; label: string; primary: boolean; destructive: boolean; href?: string | null }[];
  logs_command: string | null;
  raw_check_id: string;
  resolved: boolean;
  /** Set when this incident was created by the Policy Engine — see PolicyEvaluation. */
  policy?: IncidentPolicyApi | null;
  /** Incident Intelligence Engine — see docs/development/INCIDENT_INTELLIGENCE.md. */
  timeline?: TimelineEventApi[];
  root_cause?: RootCauseAnalysisApi | null;
  operational_impact?: OperationalImpactApi | null;
  recommendations?: RecommendationApi[];
  narrative?: IncidentNarrativeApi | null;
  relationships?: IncidentRelationshipsApi | null;
  correlated_incident_ids?: string[];
};

export type IncidentLifecyclePhaseApi =
  | "new"
  | "under_investigation"
  | "mitigation_running"
  | "verification"
  | "resolved"
  | "closed";

export type IncidentActionResultApi = {
  success: boolean;
  message: string;
  action_id: string;
  incident_id: string;
  verification: Record<string, unknown>;
  verification_passed?: boolean;
  policy_summary?: string | null;
  incident_resolved?: boolean | null;
  next_step?: string | null;
  diagnostics_available?: boolean;
  execution_id?: string | null;
};

export type ServiceOverviewApi = {
  id: string;
  name: string;
  status: "green" | "yellow" | "red" | "unknown";
  last_checked: string;
  description: string;
  affected_tenants: number;
  has_live_data: boolean;
};

export type ServiceDetailApi = {
  has_live_data: boolean;
  status_label: string;
  last_checks: { label: string; result: string; message: string; at: string }[];
  affected_tenants: string[];
  recent_events: { title: string; at: string }[];
  suggested_actions: { label: string; description: string }[];
};

export type ActivityEventApi = {
  id: string;
  category: "deployment" | "runtime" | "backup" | "ai" | "system";
  title: string;
  detail: string;
  tenant_slug?: string | null;
  timestamp: string;
};

export type OperationsAssistantBriefingApi = {
  greeting_period: "morning" | "afternoon" | "evening" | "night";
  platform_status: string;
  headline: string;
  detail_lines: string[];
  customer_impact: "none" | "low" | "medium" | "high";
  primary_incident_id: string | null;
  review_button_label: string;
  estimated_work_minutes: number | null;
  ai_confidence: number | null;
  cause_summary: string | null;
  tenant_name: string | null;
  recommended_mitigation: string | null;
  expected_recovery_seconds: number | null;
};

export type IncidentMitigationApi = {
  primary_action_id: string;
  primary_action_label: string;
  alternative_actions: { id: string; label: string; primary: boolean; destructive: boolean; href?: string | null }[];
  expected_recovery_seconds: number | null;
  estimated_risk: "low" | "medium" | "high";
  confidence: number | null;
  confidence_reasons: string[];
  summary: string;
  what: string;
  why: string;
  expected_outcome: string;
};

export type PlatformOperationsApi = {
  pulse: PlatformPulseApi;
  assistant: OperationsAssistantBriefingApi;
  attention: AttentionIncidentApi[];
  tenants: TenantHealthApi[];
  operator_tasks: OperatorTaskApi[];
  services: ServiceOverviewApi[];
  service_details: Record<string, ServiceDetailApi>;
  activity: ActivityEventApi[];
  platform_verifications?: Array<Record<string, unknown>>;
  host_verifications?: Array<Record<string, unknown>>;
  storage_health?: StorageHealthApi | null;
};

export type DoctorRunApi = {
  preview: boolean;
  verdict: "healthy" | "degraded" | "unhealthy" | "unknown";
  summary: DoctorSummaryApi;
  last_runtime_doctor: string | null;
  tenants_scanned: number;
  verifications?: Array<Record<string, unknown>>;
};

export type ComponentVersionApi = {
  name: string;
  git_commit: string;
  git_branch: string;
  build_version: string;
  build_timestamp: string;
  github_run_id: string;
  image_tag: string;
  image_digest: string;
  version: string;
};

export type PlatformVersionApi = {
  app_name: string;
  environment: string;
  git_commit: string;
  git_branch: string;
  build_timestamp: string;
  github_run_id: string;
  control_plane: ComponentVersionApi;
  operations_dashboard: ComponentVersionApi;
};

export type TokenResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

export type AdminProfile = {
  id: string;
  email: string;
  role: string;
};

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(resolveApiPath(path), { ...options, headers });
  if (res.status === 401) {
    clearToken();
    throw new Error("AUTH_REQUIRED");
  }
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body && typeof body.detail === "object" && body.detail?.message) {
        detail = body.detail.next_step
          ? `${body.detail.message} Next: ${body.detail.next_step}`
          : body.detail.message;
      } else {
        detail = body.detail || JSON.stringify(body);
      }
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const res = await fetch(resolveApiPath("/api/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let detail = "Sign in failed";
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function fetchMe(): Promise<AdminProfile> {
  return apiRequest<AdminProfile>("/api/auth/me");
}

export async function fetchPlatformPulse(): Promise<PlatformPulseApi> {
  return apiRequest<PlatformPulseApi>("/api/platform/pulse");
}

export async function fetchPlatformOperations(): Promise<PlatformOperationsApi> {
  return apiRequest<PlatformOperationsApi>("/api/platform/operations");
}

export async function runPlatformDoctor(): Promise<DoctorRunApi> {
  return apiRequest<DoctorRunApi>("/api/platform/doctor/run", { method: "POST" });
}

export async function fetchPlatformVersion(): Promise<PlatformVersionApi> {
  return apiRequest<PlatformVersionApi>("/api/platform/version");
}

export type TenantLogsApi = {
  slug: string;
  compose_project: string;
  command: string;
  lines: string[];
  fetched_at: string;
  available: boolean;
  message: string | null;
  tail: number;
};

export async function fetchTenantLogs(
  slug: string,
  tail = 200,
): Promise<TenantLogsApi> {
  const q = new URLSearchParams({ tail: String(tail) });
  return apiRequest<TenantLogsApi>(
    `/api/platform/tenants/${encodeURIComponent(slug)}/logs?${q.toString()}`,
  );
}

export async function fetchIncidentDetail(incidentId: string): Promise<IncidentDetailApi> {
  return apiRequest<IncidentDetailApi>(`/api/platform/incidents/${encodeURIComponent(incidentId)}`);
}

export type VerificationHistoryItemApi = {
  tenant_slug: string;
  component: string;
  category?: string;
  status: string;
  verification_method?: string;
  timestamp?: string | null;
  duration_ms?: number | null;
  endpoint?: string | null;
  evidence?: { label: string; value: string }[];
  recommendation?: string | null;
  confidence?: string | null;
  freshness?: string | null;
  deployment_id?: string | null;
  incident_id?: string | null;
  request_id?: string | null;
  trace_id?: string | null;
  raw?: Record<string, unknown>;
};

export type VerificationHistoryResponseApi = {
  count: number;
  results: VerificationHistoryItemApi[];
};

export type VerificationComponentsResponseApi = {
  count: number;
  components: VerificationHistoryItemApi[];
};

export type VerificationQueryParams = {
  tenant?: string;
  component?: string;
  category?: string;
  status?: string;
  deployment_id?: string;
  incident_id?: string;
  since?: string;
  until?: string;
  limit?: number;
};

function verificationQueryString(params: VerificationQueryParams = {}): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== "") q.set(key, String(value));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** Most recent VerificationResult per component — GET /api/verification/latest */
export async function fetchVerificationLatest(
  params: VerificationQueryParams = {},
): Promise<VerificationComponentsResponseApi> {
  return apiRequest<VerificationComponentsResponseApi>(
    `/api/verification/latest${verificationQueryString(params)}`,
  );
}

/** Full verification history — GET /api/verification/history */
export async function fetchVerificationHistory(
  params: VerificationQueryParams = {},
): Promise<VerificationHistoryResponseApi> {
  return apiRequest<VerificationHistoryResponseApi>(
    `/api/verification/history${verificationQueryString(params)}`,
  );
}

/** Every VerificationResult correlated to an incident — GET /api/verification/incidents/{id} */
export async function fetchVerificationForIncident(
  incidentId: string,
  limit = 500,
): Promise<VerificationHistoryResponseApi> {
  const q = new URLSearchParams({ limit: String(limit) });
  return apiRequest<VerificationHistoryResponseApi>(
    `/api/verification/incidents/${encodeURIComponent(incidentId)}?${q.toString()}`,
  );
}

/** Every VerificationResult correlated to a deployment — GET /api/verification/deployments/{id} */
export async function fetchVerificationForDeployment(
  deploymentId: string,
  limit = 500,
): Promise<VerificationHistoryResponseApi> {
  const q = new URLSearchParams({ limit: String(limit) });
  return apiRequest<VerificationHistoryResponseApi>(
    `/api/verification/deployments/${encodeURIComponent(deploymentId)}?${q.toString()}`,
  );
}

export async function executeIncidentAction(
  incidentId: string,
  actionId: string,
  options?: { confirmed?: boolean },
): Promise<IncidentActionResultApi> {
  const q = options?.confirmed ? "?confirmed=true" : "";
  return apiRequest<IncidentActionResultApi>(
    `/api/platform/incidents/${encodeURIComponent(incidentId)}/actions/${encodeURIComponent(actionId)}${q}`,
    { method: "POST" },
  );
}

export async function executePlatformAction(
  actionId: string,
  body: {
    tenant_slug?: string | null;
    incident_id?: string | null;
    confirmed?: boolean;
    params?: Record<string, string>;
  },
): Promise<IncidentActionResultApi & { requested_action_id?: string }> {
  return apiRequest(
    `/api/platform/actions/${encodeURIComponent(actionId)}/execute`,
    {
      method: "POST",
      body: JSON.stringify({
        tenant_slug: body.tenant_slug ?? null,
        incident_id: body.incident_id ?? null,
        confirmed: Boolean(body.confirmed),
        params: body.params ?? {},
      }),
    },
  );
}

export type CustomerApi = {
  id: string;
  slug: string;
  display_name: string;
  status: string;
  api_base_url: string | null;
  support_inbox_url: string | null;
  api_host_port: number | null;
  stack_project: string;
  image_tag: string;
  last_health_status: string | null;
  last_health_at: string | null;
  provision_error: string | null;
  provision_started_at: string | null;
  provision_completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export async function getCustomer(slug: string): Promise<CustomerApi> {
  return apiRequest<CustomerApi>(`/api/customers/${encodeURIComponent(slug)}`);
}

export type KnowledgeStatusApi = {
  ready: boolean;
  pdf_count: number;
  chunks_indexed: number | null;
  manuals_dir: string | null;
  health_probed: boolean;
  detail: string;
  source: string;
};

export async function getKnowledgeStatus(slug: string): Promise<KnowledgeStatusApi> {
  return apiRequest<KnowledgeStatusApi>(
    `/api/customers/${encodeURIComponent(slug)}/knowledge`,
  );
}

export type IntegrationStatusApi = {
  id: string;
  name: string;
  description?: string;
  status: "connected" | "not_configured";
  configured: boolean;
  connected: boolean;
  last_verification: string;
  last_test_ok: boolean | null;
  last_tested_at: string | null;
  last_message: string | null;
};

export async function listIntegrations(slug: string): Promise<IntegrationStatusApi[]> {
  return apiRequest<IntegrationStatusApi[]>(
    `/api/customers/${encodeURIComponent(slug)}/integrations`,
  );
}

export type DnsStatusApi = {
  check_id?: string;
  state: string;
  ok: boolean;
  hostname?: string | null;
  resolved_ips?: string[];
  expected_ip?: string | null;
  matched_expected?: boolean | null;
  resolution_mode?: "direct" | "cloudflare_proxy" | string | null;
  proxied_via_cloudflare?: boolean | null;
  https_reachable?: boolean | null;
  tls_valid?: boolean | null;
  application_responds?: boolean | null;
  verifications?: Array<Record<string, unknown>>;
  last_tested_at?: string | null;
  last_message?: string | null;
};

export async function getDnsStatus(slug: string): Promise<DnsStatusApi> {
  return apiRequest<DnsStatusApi>(`/api/customers/${encodeURIComponent(slug)}/dns`);
}

export type SslStatusApi = {
  check_id?: string;
  state: string;
  ok: boolean;
  hostname?: string | null;
  issuer?: string | null;
  expires_at?: string | null;
  days_remaining?: number | null;
  hostname_matches?: boolean | null;
  verifications?: Array<Record<string, unknown>>;
  last_tested_at?: string | null;
  last_message?: string | null;
};

export async function getSslStatus(slug: string): Promise<SslStatusApi> {
  return apiRequest<SslStatusApi>(`/api/customers/${encodeURIComponent(slug)}/ssl`);
}

export type SecretInventoryItem = {
  id: string;
  name: string;
  masked: string;
  status: string;
  last_rotated_at: string | null;
  last_used_at: string | null;
  age_days: number | null;
  use_count: number;
};

export type SecurityHealthApi = {
  average_secret_age_days: number | null;
  secrets_needing_rotation_count: number;
  recent_rotations: Array<{ at: string | null; summary: string; actor: string | null }>;
  failed_rotations_count: number;
  security_findings: Array<{ severity: string; secret_id: string; message: string }>;
  secrets: SecretInventoryItem[];
};

export async function listSecrets(slug: string): Promise<SecretInventoryItem[]> {
  return apiRequest<SecretInventoryItem[]>(
    `/api/customers/${encodeURIComponent(slug)}/secrets`,
  );
}

export async function getSecurityHealth(slug: string): Promise<SecurityHealthApi> {
  return apiRequest<SecurityHealthApi>(
    `/api/customers/${encodeURIComponent(slug)}/secrets/health`,
  );
}
